import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaMobile, FaDesktop, FaChartPie, FaUserSecret } from 'react-icons/fa';
import { Container, Section, Title, Subtitle, theme } from '../../styles/GlobalStyles';

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-top: 60px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 20px;
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)<{ isHighlighted?: boolean }>`
  background: ${({ isHighlighted }) => 
    isHighlighted ? theme.gradients.accent : theme.gradients.card};
  color: ${theme.colors.text};
  padding: 40px 30px;
  border-radius: 15px;
  text-align: center;
  box-shadow: ${theme.shadows.medium};
  border: 1px solid ${theme.colors.border};
  transition: all ${theme.transitions.medium};
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${theme.gradients.accent};
    opacity: 0;
    transition: opacity ${theme.transitions.medium};
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${theme.shadows.glowAccent};
    border-color: ${theme.colors.accent};
    
    &::before {
      opacity: 0.1;
    }
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const IconWrapper = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
  opacity: 0.9;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 15px;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.9;
`;

const ServiceCards: React.FC = () => {
  const services = [
    {
      icon: <FaMobile />,
      title: 'Managed Security Services',
      description: 'Comprehensive 24/7 monitoring and management of your security infrastructure with expert SOC and NOC services.',
      isHighlighted: false
    },
    {
      icon: <FaDesktop />,
      title: 'Security Technologies',
      description: 'Advanced security solutions including EDR, SIEM, WAF, and cutting-edge threat detection technologies.',
      isHighlighted: true
    },
    {
      icon: <FaChartPie />,
      title: 'Governance, Risk & Compliance',
      description: 'Expert guidance on regulatory compliance, risk assessments, and governance frameworks including ISO 27001 and PCI DSS.',
      isHighlighted: false
    },
    {
      icon: <FaUserSecret />,
      title: 'Security Testing',
      description: 'Comprehensive penetration testing, vulnerability assessments, and red team exercises to identify and mitigate threats.',
      isHighlighted: true
    }
  ];

  return (
    <Section style={{ background: theme.colors.backgroundSecondary }}>
      <Container>
        <Title>Our Core Service Pillars</Title>
        <Subtitle>
          Comprehensive cybersecurity solutions designed to protect, monitor, and enhance your organization's security posture
        </Subtitle>
        
        <ServiceGrid>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              isHighlighted={service.isHighlighted}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <IconWrapper>{service.icon}</IconWrapper>
              <CardTitle>{service.title}</CardTitle>
              <CardDescription>{service.description}</CardDescription>
            </ServiceCard>
          ))}
        </ServiceGrid>
      </Container>
    </Section>
  );
};

export default ServiceCards;

